import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * v0.19 – tối ưu khu nhà ở: một ngôi nhà dựng từ hàng trăm khối nhỏ ⇒ gộp các khối tĩnh cùng vật liệu thành một lưới
 * (≈ 25 lệnh vẽ / nhà thay vì ≈ 100). Khối nhỏ (nội thất, đồ thờ, vườn, đèn, bảng tên…) gom vào một LOD:
 * chỉ vẽ khi camera ở gần (mặc định ≤ 48 m) và không đổ bóng. Hình dáng, màu sắc giữ nguyên – không giảm độ nét.
 */
const KEEP_ATTR = ['position', 'normal', 'uv'];
const tmp = new THREE.Matrix4();
const sphere = new THREE.Sphere();

function prep(src: THREE.BufferGeometry, mat: THREE.Matrix4) {
  const g = src.index ? src.toNonIndexed() : src.clone();
  for (const k of Object.keys(g.attributes)) if (!KEEP_ATTR.includes(k)) g.deleteAttribute(k);
  if (!g.attributes.normal) g.computeVertexNormals();
  if (!g.attributes.uv) g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
  g.clearGroups(); g.morphAttributes = {};
  g.applyMatrix4(mat);
  return g;
}

export function mergeHouse(root: THREE.Group, opts: { keep: Iterable<THREE.Object3D>; shadows: boolean; detailRadius?: number; detailDistance?: number }) {
  const keep = new Set(opts.keep); const detailR = opts.detailRadius ?? 0.9;
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const buckets = new Map<string, { mat: THREE.Material; geos: THREE.BufferGeometry[]; cast: boolean; recv: boolean; detail: boolean }>();
  const drop: THREE.Mesh[] = []; const moveToDetail: THREE.Object3D[] = [];
  const walk = (o: THREE.Object3D) => {
    if (o !== root && (keep.has(o) || o.userData.lantern || (o as THREE.Sprite).isSprite)) { if (o.parent === root) moveToDetail.push(o); return; }
    for (const c of [...o.children]) walk(c);
    const me = o as THREE.Mesh;
    if (o === root || !me.isMesh || (me as THREE.InstancedMesh).isInstancedMesh || Array.isArray(me.material) || !me.geometry?.attributes.position) return;
    tmp.multiplyMatrices(inv, me.matrixWorld);
    if (!me.geometry.boundingSphere) me.geometry.computeBoundingSphere();
    sphere.copy(me.geometry.boundingSphere!).applyMatrix4(tmp);
    const detail = sphere.radius < detailR;
    const key = `${me.material.uuid}|${detail ? 1 : 0}`;
    const b = buckets.get(key) ?? { mat: me.material, geos: [], cast: false, recv: false, detail }; buckets.set(key, b);
    b.geos.push(prep(me.geometry, tmp)); b.cast ||= me.castShadow; b.recv ||= me.receiveShadow;
    drop.push(me);
  };
  walk(root);
  const det = new THREE.Group(); det.name = 'house-detail';
  for (const b of buckets.values()) {
    const merged = b.geos.length === 1 ? b.geos[0] : mergeGeometries(b.geos, false);
    if (b.geos.length > 1) b.geos.forEach((g) => g.dispose());
    if (!merged) continue;
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, b.mat);
    mesh.castShadow = opts.shadows && b.cast && !b.detail; mesh.receiveShadow = opts.shadows && b.recv;
    (b.detail ? det : root).add(mesh);
  }
  for (const me of drop) { me.removeFromParent(); me.geometry.dispose(); }
  for (const o of moveToDetail) det.add(o); // vật động (lửa nến, cá, đèn lồng, ánh hào quang, bảng tên): vẫn cập nhật, chỉ vẽ khi ở gần
  const lod = new THREE.LOD(); lod.addLevel(det, 0); lod.addLevel(new THREE.Object3D(), opts.detailDistance ?? 48); lod.name = 'house-lod';
  root.add(lod);
  return root;
}
